/**
 * ContractsView — the global Contracts & Proposals surface (Files >
 * Contracts). Subscribes to the whole `documents` collection and lets
 * you filter by kind (proposals / contracts) and status, search by
 * title or account, and open any document inline to edit / set status /
 * export the branded PDF. Generation stays on the entity pages (an
 * account or prospect owns the relationship); this view is the org-wide
 * roll-up of everything already created.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Camera,
  Clock,
  Eye,
  FileSignature,
  FileText,
  Lock,
  Minus,
  PenLine,
  Plus,
  Printer,
  Search,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { DocumentRow } from '../components/DocumentsPanel';
import { NewProposalModal } from '../components/NewProposalModal';
import { ProposalBodyEditor } from '../components/ProposalBodyEditor';
import { ProposalPreviewModal } from '../components/ProposalPreviewModal';
import { useAuth } from '../lib/auth/AuthProvider';
import { C } from '../lib/tokens';
import {
  bootstrapDocumentsIfEmpty,
  setDocumentStatus,
  subscribeDocuments,
} from '../lib/db/documents';
import {
  bootstrapAccountsIfEmpty,
  subscribeAccounts,
} from '../lib/db/accounts';
import { logActivity } from '../lib/db/activity';
import {
  assertDocUnchangedSince,
  buildCertificate,
  captureSignature,
  createSignaturePad,
  initSigning,
  isPadComplete,
  padValue,
  setTypedValue,
  verifySignatureChain,
  type CompletionCertificate,
  type Signer,
} from '../lib/crm/proposalSignature';
import { defaultLineItemsForDoc } from '../lib/crm/proposalDoc';
import { renderDocumentHtml } from '../lib/crm/proposalTemplates';
import {
  applyBuyerSelection,
  assertEditable,
  computeTotals,
  snapshotVersion,
  type BrandKit,
  type PricingConfig,
  type ProposalBlock,
} from '../lib/crm/proposalBlocks';
import { buildModelForDoc } from '../lib/crm/businessDocModel';
import {
  chaseApprovalAction,
  detectChaseSignals,
  draftChase,
  gateChase,
  logFromDocSnapshot,
  summarizeEngagement,
  type ChaseSignal,
  type ChaseWorker,
} from '../lib/crm/proposalEngagement';
import { chaseToApprovalAction, decideChase } from '../lib/agent/proposalChase';
import { queueApproval } from '../lib/agent/approvals';
import { computeDocHash } from '../lib/crm/docHash';
import {
  applyTransition,
  type SignatureRequest,
} from '../lib/crm/signatureRequest';
import {
  buildHashFingerprintLabel,
  buildStatusChip,
} from '../lib/crm/signatureRequestViewModel';
import {
  createSignatureRequest,
  listSignatureRequestsByDoc,
  updateSignatureRequest,
} from '../lib/db/signatureRequests';
import {
  DEFAULT_SIGNER_LINK_SALT,
  buildSignLink,
  issueSignerToken,
} from '../lib/crm/signerToken';
import { isConfigured as workerConfigured, runAgent } from '../lib/agent/argoWorker';
import type {
  Account,
  BusinessDoc,
  DocumentKind,
  DocumentStatus,
} from '../lib/db/types';

type KindFilter = 'all' | DocumentKind;

const STATUS_ORDER: DocumentStatus[] = [
  'draft',
  'sent',
  'viewed',
  'accepted',
  'signed',
  'declined',
  'expired',
];

/**
 * "Won" terminal states — a proposal that was `accepted` or a contract
 * that was `signed` (see BusinessDoc.acceptedAt). These are closed-won,
 * NOT open pipeline, so the pipeline metric must exclude them.
 */
const WON_STATUSES: DocumentStatus[] = ['accepted', 'signed'];
/** Lost terminal states — neither open pipeline nor won. */
const LOST_STATUSES: DocumentStatus[] = ['declined', 'expired'];

/**
 * Preview / Print (Ask #5) — open the branded, sectioned, print-ready
 * document in a new tab. All layout lives in the PURE
 * `renderDocumentHtml` router: proposals with a branded templateId
 * (aurora / onyx / blueprint — picked at create time) get that branded
 * design, legacy proposals get the original sales template, and
 * contracts get the legally-structured agreement (parties / recitals /
 * numbered clauses / execution block).
 * This shim only handles the window. Line items derive from the doc's
 * own recorded value (never fabricated) until a structured line-item
 * editor lands; the account (when known) feeds the POC into the
 * signature/execution block.
 */
function previewProposal(doc: BusinessDoc, account?: Account | null): void {
  const html = renderDocumentHtml(doc, {
    account: account ?? null,
    lineItems: defaultLineItemsForDoc(doc),
  });
  const win = window.open('', '_blank');
  if (!win) {
    window.alert(`Allow pop-ups to preview & print this ${doc.kind}.`);
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

function usd(n?: number): string {
  if (n == null) return '—';
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function ContractsView() {
  const { user } = useAuth();
  const canMutate = user?.role !== 'viewer';
  const [docs, setDocs] = useState<BusinessDoc[]>([]);
  const [kind, setKind] = useState<KindFilter>('all');
  const [status, setStatus] = useState<DocumentStatus | 'all'>('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  /** Which create dialog is open (null = closed) — proposal or contract. */
  const [newDocKind, setNewDocKind] = useState<DocumentKind | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  /** Doc id shown in the in-app branded preview modal (null = closed). */
  const [previewId, setPreviewId] = useState<string | null>(null);

  useEffect(() => {
    bootstrapDocumentsIfEmpty().catch(() => undefined);
    const unsub = subscribeDocuments((all) => {
      setDocs(
        [...all].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1)),
      );
    });
    return unsub;
  }, []);

  // Accounts feed the New-proposal modal's account picker.
  useEffect(() => {
    bootstrapAccountsIfEmpty().catch(() => undefined);
    return subscribeAccounts(setAccounts);
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return docs.filter((d) => {
      if (kind !== 'all' && d.kind !== kind) return false;
      if (status !== 'all' && d.status !== status) return false;
      if (
        needle &&
        !d.title.toLowerCase().includes(needle) &&
        !d.entityName.toLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [docs, kind, status, q]);

  // Open pipeline value = dollars in documents that are still in play —
  // i.e. NOT yet won (accepted/signed) and NOT lost (declined/expired).
  // Including won docs here would mislabel closed revenue as "open
  // pipeline" and double-count it against the separate Won figure below.
  const pipelineValue = useMemo(
    () =>
      docs
        .filter(
          (d) =>
            !WON_STATUSES.includes(d.status) &&
            !LOST_STATUSES.includes(d.status),
        )
        .reduce((sum, d) => sum + (d.value ?? 0), 0),
    [docs],
  );

  // Won value = dollars in documents that have closed (accepted/signed),
  // surfaced alongside open pipeline so the split is honest.
  const wonValue = useMemo(
    () =>
      docs
        .filter((d) => WON_STATUSES.includes(d.status))
        .reduce((sum, d) => sum + (d.value ?? 0), 0),
    [docs],
  );

  // Status → count, for the filter chips (over the kind-filtered set so
  // the counts track the proposals/contracts toggle).
  const statusCounts = useMemo(() => {
    const base = kind === 'all' ? docs : docs.filter((d) => d.kind === kind);
    const counts: Record<string, number> = {};
    for (const d of base) counts[d.status] = (counts[d.status] ?? 0) + 1;
    return counts;
  }, [docs, kind]);

  const kindCounts = useMemo(
    () => ({
      all: docs.length,
      proposal: docs.filter((d) => d.kind === 'proposal').length,
      contract: docs.filter((d) => d.kind === 'contract').length,
    }),
    [docs],
  );

  // Keep the status filter coherent with the kind toggle. The status chips
  // only render for statuses present in the current kind (statusCounts > 0),
  // so a status selected under one kind (e.g. 'signed' on contracts) would
  // otherwise stay silently applied after switching to a kind with none of
  // them — its chip vanishes while the list filters to empty with no active
  // chip to explain why. Reset to 'all' whenever the active status drops out.
  useEffect(() => {
    if (status !== 'all' && (statusCounts[status] ?? 0) === 0) {
      setStatus('all');
    }
  }, [statusCounts, status]);

  return (
    <div className="app-container" style={{ display: 'grid', gap: 16 }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <span className="t-eyebrow">Files</span>
          <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>
            Contracts &amp; Proposals
          </h1>
          <p className="t-meta" style={{ margin: '6px 0 0' }}>
            Every agreement across the org, by status. Start a new proposal
            right here, or generate one from an account or prospect; track and
            export them all in this view.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                top: '50%',
                left: 12,
                transform: 'translateY(-50%)',
                color: C.textDim,
              }}
            />
            <input
              className="glass-input"
              placeholder="Search by title or account…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ paddingLeft: 34, width: 260 }}
            />
          </div>
          {canMutate && (
            <>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={() => setNewDocKind('proposal')}
              >
                <Plus size={13} /> New proposal
              </button>
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={() => setNewDocKind('contract')}
              >
                <FileSignature size={13} /> New contract
              </button>
            </>
          )}
        </div>
      </header>

      {previewId &&
        (() => {
          const doc = docs.find((x) => x.id === previewId);
          if (!doc) return null;
          const account =
            doc.entityType === 'account'
              ? accounts.find((a) => a.id === doc.entityId) ?? null
              : null;
          return (
            <ProposalPreviewModal
              doc={doc}
              account={account}
              onClose={() => setPreviewId(null)}
            />
          );
        })()}

      {newDocKind && (
        <NewProposalModal
          accounts={accounts}
          docKind={newDocKind}
          ownerName={user?.displayName || undefined}
          createdBy={user?.email}
          onClose={() => setNewDocKind(null)}
          onCreated={(doc) => {
            setNewDocKind(null);
            // Make the new document visible + open regardless of active
            // filters: it's a fresh draft, so clear anything that would
            // hide it, then open it inline.
            setKind('all');
            setStatus('all');
            setQ('');
            setOpenId(doc.id);
          }}
        />
      )}

      {/* Summary stat row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <StatCard label="Documents" value={String(docs.length)} />
        <StatCard label="Proposals" value={String(kindCounts.proposal)} />
        <StatCard label="Contracts" value={String(kindCounts.contract)} />
        <StatCard label="Open pipeline value" value={usd(pipelineValue)} />
        <StatCard label="Won value" value={usd(wonValue)} />
      </div>

      {/* Filters */}
      <GlassPanel style={{ padding: 12 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Kind segmented control */}
          <div style={{ display: 'flex', gap: 6 }}>
            {(['all', 'proposal', 'contract'] as KindFilter[]).map((k) => {
              const isActive = kind === k;
              const label =
                k === 'all'
                  ? 'All'
                  : k === 'proposal'
                    ? 'Proposals'
                    : 'Contracts';
              return (
                <button
                  key={k}
                  className="glass-btn"
                  onClick={() => setKind(k)}
                  style={{
                    background: isActive
                      ? 'rgba(255,0,255,0.14)'
                      : 'transparent',
                    color: isActive ? '#fff' : C.textMuted,
                    boxShadow: isActive
                      ? 'inset 0 0 0 1px rgba(255,0,255,0.45)'
                      : undefined,
                  }}
                >
                  {k === 'proposal' && <FileText size={13} />}
                  {k === 'contract' && <FileSignature size={13} />}
                  {label}
                  <span className="t-meta" style={{ marginLeft: 4 }}>
                    {kindCounts[k]}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Status chips */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <StatusChip
              label="Any status"
              active={status === 'all'}
              onClick={() => setStatus('all')}
            />
            {STATUS_ORDER.filter((s) => (statusCounts[s] ?? 0) > 0).map((s) => (
              <StatusChip
                key={s}
                label={`${s} (${statusCounts[s]})`}
                active={status === s}
                onClick={() => setStatus(s)}
              />
            ))}
          </div>
        </div>
      </GlassPanel>

      {/* List */}
      {filtered.length === 0 ? (
        <GlassPanel style={{ padding: 0 }}>
          <div
            className="t-meta"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <FileSignature size={26} strokeWidth={1.6} color={C.textDim} />
            {docs.length === 0
              ? 'No documents yet. Generate a proposal or contract from an account or prospect.'
              : 'No documents match these filters.'}
          </div>
        </GlassPanel>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {filtered.map((d) => (
            <div key={d.id} style={{ display: 'grid', gap: 6 }}>
              <DocumentRow
                doc={d}
                open={openId === d.id}
                onToggle={() =>
                  setOpenId((id) => (id === d.id ? null : d.id))
                }
                canMutate={canMutate}
              />
              {d.kind === 'proposal' && openId === d.id && (
                <div style={{ display: 'grid', gap: 6 }}>
                  <InteractiveProposalPanel doc={d} />
                  <ProposalEngagementPanel doc={d} canMutate={canMutate} />
                </div>
              )}
              {openId === d.id && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="glass-btn"
                    onClick={() => setPreviewId(d.id)}
                    title={
                      d.kind === 'contract'
                        ? 'Preview the print-ready agreement (parties, clauses, execution block)'
                        : 'Preview the branded proposal document in-app'
                    }
                  >
                    <Printer size={13} /> Preview
                  </button>
                  <button
                    className="glass-btn"
                    onClick={() =>
                      previewProposal(
                        d,
                        d.entityType === 'account'
                          ? accounts.find((a) => a.id === d.entityId) ?? null
                          : null,
                      )
                    }
                    title="Open in a print tab (classic flow)"
                  >
                    Print tab
                  </button>
                </div>
              )}
              {openId === d.id && d.kind === 'proposal' && (
                <ProposalBodyEditor
                  doc={d}
                  account={
                    d.entityType === 'account'
                      ? accounts.find((a) => a.id === d.entityId) ?? null
                      : null
                  }
                  canMutate={canMutate}
                />
              )}
              {openId === d.id && <ESignPanel doc={d} canMutate={canMutate} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <GlassPanel style={{ padding: '14px 16px' }}>
      <div className="t-eyebrow">{label}</div>
      <div
        className="t-section-title"
        style={{ margin: '4px 0 0', fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </div>
    </GlassPanel>
  );
}

function StatusChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="glass-pill"
      style={{
        cursor: 'pointer',
        textTransform: 'capitalize',
        color: active ? '#fff' : C.textMuted,
        borderColor: active
          ? 'rgba(255,0,255,0.5)'
          : 'rgba(255,255,255,0.14)',
        background: active ? 'rgba(255,0,255,0.14)' : 'rgba(255,255,255,0.04)',
        fontWeight: active ? 700 : 500,
      }}
    >
      {label}
    </button>
  );
}

// ===========================================================================
// Engagement analytics + governed agentic chase (proposalEngagement.ts wiring)
// ===========================================================================

/** Human label per chase reason for the signal rows. */
const CHASE_REASON_LABEL: Record<ChaseSignal['reason'], string> = {
  unopened: 'Never opened',
  viewed_no_action: 'Viewed, no action',
  stalled_on_pricing: 'Stalled on pricing',
  expiring_soon: 'Expiring soon',
};

/**
 * Adapt the REAL Argo agent-worker (argoWorker.runAgent) onto the tiny
 * drafting seam proposalEngagement expects. Returns undefined when no
 * worker endpoint is configured, so draftChase takes its deterministic
 * fallback — the whole panel works offline.
 */
function chaseWorkerAdapter(): ChaseWorker | undefined {
  if (!workerConfigured()) return undefined;
  return {
    generate: async (prompt: string) => {
      const out = await runAgent(
        {
          id: 'proposal-chase',
          name: 'Proposal Chase',
          template: 'proposal_chase',
          persona: 'Engagement-driven proposal follow-up drafter',
        },
        { prompt },
      );
      return {
        text: out.text,
        reasoning: 'Drafted by the Argo agent-worker from live engagement analytics.',
      };
    },
  };
}

/**
 * Engagement panel for one open proposal: honest view analytics from the
 * doc's own recorded status (summarizeEngagement over logFromDocSnapshot —
 * nothing fabricated) plus the governed chase. Each detected signal offers
 * a "Draft chase" action that drafts via the real worker (deterministic
 * fallback when offline) and ALWAYS emits an approvals-inbox item — never
 * a direct send. The same telemetry is run through the proposalChase
 * scheduler engine (decideChase → chaseToApprovalAction) so its verdict
 * rides along on the approval note.
 */
function ProposalEngagementPanel({
  doc,
  canMutate,
}: {
  doc: BusinessDoc;
  canMutate: boolean;
}) {
  const [busyReason, setBusyReason] = useState<ChaseSignal['reason'] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const now = Date.now();
  const log = logFromDocSnapshot(doc);
  const sentAtMs = Date.parse(doc.createdAt);
  const sentAt = Number.isFinite(sentAtMs) ? sentAtMs : undefined;
  const validUntilMs = doc.validUntil ? Date.parse(doc.validUntil) : Number.NaN;
  const validUntil = Number.isFinite(validUntilMs)
    ? validUntilMs
    : Number.POSITIVE_INFINITY;

  const summary = summarizeEngagement(log, { now, sentAt });
  // Draft docs haven't been sent — there is nothing out there to chase yet.
  const signals =
    doc.status === 'draft'
      ? []
      : detectChaseSignals({ log, validUntil, now, sentAt });

  const queueChase = async (signal: ChaseSignal) => {
    setBusyReason(signal.reason);
    setError(null);
    setNotice(null);
    try {
      const draft = await draftChase(
        signal,
        { account: doc.entityName, engagement: summary },
        chaseWorkerAdapter(),
      );
      // No autonomy has been earned for customer-facing sends in this
      // deployment: tier 0 gates every draft to the approvals inbox.
      const gate = gateChase(draft, { tier: 0, maxAutoSeverity: 0 });
      // Exercise the proposalChase scheduler engine on the same telemetry
      // so its verdict is auditable alongside the drafted copy.
      const legacyMove = decideChase(
        {
          state: doc.status,
          viewCount: summary.opens,
          lastViewedAt: summary.lastOpenTs,
          sentAt: sentAt ?? null,
          mostViewedSection: summary.hottestSectionId,
          now,
        },
        { staleHours: 72, viewThreshold: 1 },
      );
      const legacy = chaseToApprovalAction(legacyMove);
      const note = [
        `Chase (${CHASE_REASON_LABEL[signal.reason]}): ${draft.subject}`,
        draft.body,
        `Why: ${draft.reasoning}`,
        `Gate: ${gate.action} — ${gate.reason}`,
        legacy ? `Telemetry engine: ${legacy.reason}` : null,
      ]
        .filter(Boolean)
        .join('\n');
      await queueApproval(
        chaseApprovalAction(draft, {
          account: doc.entityName,
          accountId: doc.entityType === 'account' ? doc.entityId : undefined,
        }),
        'scheduler',
        note,
      );
      setNotice(
        'Follow-up drafted and queued for approval — nothing was sent to the customer.',
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to queue the chase.');
    } finally {
      setBusyReason(null);
    }
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Eye size={14} color={C.textDim} />
        <span className="t-section-title">Engagement &amp; chase</span>
        <span className="t-meta" style={{ marginLeft: 'auto' }}>
          {workerConfigured()
            ? 'Drafts via Argo agent-worker'
            : 'Offline — deterministic drafts'}
        </span>
      </div>

      {/* Analytics row — honest numbers from the doc's own recorded status. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span className="glass-pill">
          {summary.opens} open{summary.opens === 1 ? '' : 's'}
        </span>
        <span className="glass-pill">
          {summary.uniqueRecipients} recipient
          {summary.uniqueRecipients === 1 ? '' : 's'}
        </span>
        <span className="glass-pill">
          {Math.round(summary.totalDwellMs / 1000)}s dwell
        </span>
        <span className="glass-pill">
          Hottest: {summary.hottestSectionId ?? '—'}
        </span>
        {summary.stalled && (
          <span className="glass-pill" style={{ color: '#ffb4b4' }}>
            Stalled
          </span>
        )}
        {doc.validUntil && (
          <span className="glass-pill">
            <Clock size={11} style={{ marginRight: 4, verticalAlign: '-1px' }} />
            Valid until {doc.validUntil}
          </span>
        )}
      </div>

      {log.events.length === 0 && (
        <p className="t-meta" style={{ margin: 0 }}>
          No buyer engagement recorded yet — analytics appear once the
          proposal is viewed. Nothing here is fabricated.
        </p>
      )}

      {/* Chase signals — every action emits an approval item, never a send. */}
      {signals.length === 0 ? (
        <p className="t-meta" style={{ margin: 0 }}>
          No chase warranted right now — silence is a valid, honest decision.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {signals.map((signal) => (
            <div
              key={signal.reason}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span className="glass-pill" style={{ color: '#fff' }}>
                {CHASE_REASON_LABEL[signal.reason]}
              </span>
              <span className="t-meta">severity {signal.severity}</span>
              {canMutate && (
                <button
                  className="glass-btn glass-btn--primary"
                  disabled={busyReason !== null}
                  onClick={() => queueChase(signal)}
                  title="Draft a follow-up and queue it for approval — never a direct send"
                  style={{ marginLeft: 'auto' }}
                >
                  <Send size={13} />
                  {busyReason === signal.reason
                    ? 'Drafting…'
                    : 'Draft chase (needs approval)'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {notice && (
        <p className="t-meta" style={{ margin: 0, color: '#9fe8b8' }}>
          {notice}
        </p>
      )}
      {error && (
        <p className="t-meta" style={{ margin: 0, color: '#ffb4b4' }}>
          {error}
        </p>
      )}
    </GlassPanel>
  );
}

// ===========================================================================
// Interactive block-based proposal (proposalBlocks.ts wiring)
// ===========================================================================

// The derivation (BusinessDoc → block model) + brand kit now live in
// src/lib/crm/businessDocModel.ts so the public sign page (SignView) hashes
// the EXACT same canonical bytes this view issues signature requests over.

/**
 * Pricing derives from the document's own recorded value — a single
 * required engagement line. We NEVER invent line items or prices; docs
 * without a value get an honest empty state instead.
 */
function pricingForDoc(doc: BusinessDoc): PricingConfig {
  return {
    currency: 'USD',
    lineItems:
      doc.value != null
        ? [
            {
              id: `${doc.id}-engagement`,
              label: `Engagement — ${doc.entityName}`,
              unitPrice: doc.value,
              qty: 1,
              optional: false,
              selected: true,
              minQty: 1,
              maxQty: 99,
              recurring: null,
            },
          ]
        : [],
  };
}

/** Statuses in which the buyer may still reconfigure pricing. */
const BUYER_INTERACTIVE_STATUSES: DocumentStatus[] = ['draft', 'sent', 'viewed'];

/** Map every persisted status onto assertEditable's vocabulary. */
function editGateStatus(
  status: DocumentStatus,
): 'draft' | 'sent' | 'viewed' | 'signed' {
  if (status === 'draft' || status === 'sent' || status === 'viewed') {
    return status;
  }
  return 'signed'; // accepted / signed / declined / expired are all terminal
}

/**
 * Interactive block-based proposal: typed blocks rendered with the
 * brand kit plus a live-recalculating pricing table — buyer toggles /
 * qty steppers run through `applyBuyerSelection` and `computeTotals`
 * recomputes instantly (pure local state; the approved figure is
 * written elsewhere). "Freeze version" takes a deep-frozen, hashed
 * snapshot via `snapshotVersion`; structural editing is gated by
 * `assertEditable` (locked once sent/viewed/signed).
 */
function InteractiveProposalPanel({ doc }: { doc: BusinessDoc }) {
  const model = useMemo(() => buildModelForDoc(doc), [doc]);
  const [cfg, setCfg] = useState<PricingConfig>(() => pricingForDoc(doc));
  const [versionSeq, setVersionSeq] = useState(0);
  const [snap, setSnap] = useState<{ version: number; hash: string } | null>(
    null,
  );

  // Re-derive pricing when the underlying record changes.
  useEffect(() => {
    setCfg(pricingForDoc(doc));
    setSnap(null);
    setVersionSeq(0);
  }, [doc.id, doc.value, doc.entityName]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = computeTotals(cfg);
  const interactive = BUYER_INTERACTIVE_STATUSES.includes(doc.status);

  let structurallyEditable = true;
  try {
    assertEditable(model, editGateStatus(doc.status));
  } catch {
    structurallyEditable = false;
  }

  const edit = (e: { lineItemId: string; selected?: boolean; qty?: number }) =>
    setCfg((prev) => applyBuyerSelection(prev, e));

  const freezeVersion = () => {
    const s = snapshotVersion({ ...model, versionSeq });
    setVersionSeq(s.version);
    setSnap({ version: s.version, hash: s.hash });
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 14 }}>
      {/* Version + edit-gate header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span className="t-eyebrow">Interactive proposal</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!structurallyEditable && (
            <span
              className="glass-pill"
              title="Editing after send/view/sign is blocked — create a new draft version instead"
              style={{ color: C.textMuted, display: 'inline-flex', gap: 5 }}
            >
              <Lock size={11} /> content locked ({doc.status})
            </span>
          )}
          {snap && (
            <span
              className="glass-pill"
              title="Deep-frozen snapshot with a deterministic content hash"
              style={{ color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}
            >
              v{snap.version} · {snap.hash}
            </span>
          )}
          <button
            className="glass-btn"
            onClick={freezeVersion}
            title="Take an immutable, content-hashed version snapshot"
          >
            <Camera size={13} /> Freeze version
          </button>
        </div>
      </div>

      {/* Blocks */}
      {model.blocks.map((block) => (
        <BlockRenderer key={block.id} block={block} brand={model.brandKit} />
      ))}

      {/* Live pricing table */}
      {cfg.lineItems.length === 0 ? (
        <p className="t-meta" style={{ margin: 0 }}>
          No priced line items recorded on this document — set a value on the
          proposal to enable interactive pricing.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {cfg.lineItems.map((item) => {
            const counted = !item.optional || item.selected;
            const line = Math.max(0, item.qty) * item.unitPrice;
            return (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                  opacity: counted ? 1 : 0.5,
                }}
              >
                {item.optional ? (
                  <input
                    type="checkbox"
                    checked={item.selected}
                    disabled={!interactive}
                    onChange={(e) =>
                      edit({ lineItemId: item.id, selected: e.target.checked })
                    }
                    title="Optional line — include or exclude it"
                  />
                ) : (
                  <span
                    className="t-meta"
                    title="Required line — always included"
                    style={{ width: 13, textAlign: 'center' }}
                  >
                    •
                  </span>
                )}
                <span style={{ flex: 1, minWidth: 160, color: C.text }}>
                  {item.label}
                  {item.recurring && (
                    <span className="t-meta" style={{ marginLeft: 6 }}>
                      /{item.recurring === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  )}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    className="glass-btn"
                    disabled={!interactive}
                    onClick={() => edit({ lineItemId: item.id, qty: item.qty - 1 })}
                    title="Decrease quantity"
                    style={{ padding: '2px 6px' }}
                  >
                    <Minus size={11} />
                  </button>
                  <span
                    className="t-meta"
                    style={{
                      minWidth: 24,
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                      color: C.text,
                    }}
                  >
                    {item.qty}
                  </span>
                  <button
                    className="glass-btn"
                    disabled={!interactive}
                    onClick={() => edit({ lineItemId: item.id, qty: item.qty + 1 })}
                    title="Increase quantity"
                    style={{ padding: '2px 6px' }}
                  >
                    <Plus size={11} />
                  </button>
                </div>
                <span
                  style={{
                    minWidth: 96,
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: 600,
                    color: counted ? C.text : C.textDim,
                  }}
                >
                  {usd(counted ? line : 0)}
                </span>
              </div>
            );
          })}

          {/* Totals — recompute instantly on every toggle / qty change */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 18,
              flexWrap: 'wrap',
              borderTop: '1px solid rgba(255,255,255,0.1)',
              paddingTop: 10,
            }}
          >
            <TotalCell label="One-time" value={usd(totals.oneTime)} />
            <TotalCell label="Monthly" value={usd(totals.recurringMonthly)} />
            <TotalCell label="Annual" value={usd(totals.recurringAnnual)} />
            <TotalCell label="Subtotal" value={usd(totals.subtotal)} accent />
          </div>
          {!interactive && (
            <p className="t-meta" style={{ margin: 0, textAlign: 'right' }}>
              Pricing is final — this document is {doc.status}.
            </p>
          )}
        </div>
      )}
    </GlassPanel>
  );
}

/** Render one typed block with the brand kit. Pure presentation. */
function BlockRenderer({
  block,
  brand,
}: {
  block: ProposalBlock;
  brand: BrandKit;
}) {
  switch (block.kind) {
    case 'hero':
      return (
        <div style={{ display: 'grid', gap: 6 }}>
          <div
            style={{
              height: 3,
              width: 44,
              borderRadius: 2,
              background: `linear-gradient(90deg, ${brand.accent}, ${C.magentaDeep})`,
            }}
          />
          <h3 className="t-section-title" style={{ margin: 0 }}>
            {block.headline}
          </h3>
          {block.subheadline && (
            <p className="t-meta" style={{ margin: 0 }}>
              {block.subheadline}
            </p>
          )}
          <UnresolvedTokens tokens={block.unresolvedTokens} />
        </div>
      );
    case 'text':
      return (
        <div style={{ display: 'grid', gap: 6 }}>
          <p
            className="t-meta"
            style={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              color: C.textMuted,
              maxHeight: 180,
              overflow: 'auto',
            }}
          >
            {block.text}
          </p>
          <UnresolvedTokens tokens={block.unresolvedTokens} />
        </div>
      );
    case 'image':
      return (
        <img
          src={block.url}
          alt={block.alt ?? ''}
          style={{ maxWidth: '100%', borderRadius: 8 }}
        />
      );
    case 'quote':
      return block.heading ? (
        <div className="t-eyebrow">{block.heading}</div>
      ) : null;
    case 'testimonial':
      return (
        <blockquote className="t-meta" style={{ margin: 0 }}>
          “{block.quote}” — {block.author}
          {block.role ? `, ${block.role}` : ''}
        </blockquote>
      );
  }
}

/** Amber flags for merge tokens that could not be resolved. */
function UnresolvedTokens({ tokens }: { tokens?: string[] }) {
  if (!tokens || tokens.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {tokens.map((t) => (
        <span
          key={t}
          className="glass-pill"
          title="Unresolved merge token — flagged, not blanked"
          style={{ color: C.orange, borderColor: 'rgba(253,173,1,0.4)' }}
        >
          unresolved: {`{{${t}}}`}
        </span>
      ))}
    </div>
  );
}

// ===========================================================================
// Real e-signature capture (proposalSignature.ts wiring)
// ===========================================================================

/** Statuses in which a recipient may legally execute the document. */
const SIGNABLE_STATUSES: DocumentStatus[] = ['sent', 'viewed'];

/**
 * ESIGN/UETA-style signing surface — replaces the status-flip-only accept.
 * The signer types their legal name (normalized through the pure
 * SignaturePadModel), provides their email, and checks an EXPLICIT consent
 * box; only then does `captureSignature` seal a hash-chained
 * SignatureRecord bound to the exact `snapshotVersion` hash of the document
 * they are looking at. `assertDocUnchangedSince` re-hashes the doc at click
 * time, so a mid-session edit fails the signing instead of silently binding
 * the signer to different content. On success the signer event is sealed
 * into the Decision Ledger (logActivity), the real status transition runs,
 * and the certificate of completion renders inline with its own hash.
 * userAgent is captured verbatim from the browser; the client cannot know
 * its own public IP, so that field honestly records as unavailable rather
 * than being fabricated.
 */
/**
 * The exact ESIGN/UETA consent language shown to the signer. Recorded
 * VERBATIM (text + version) on every signature request — consent is
 * evidence, not a bare boolean (auditor finding #4). Bump the version
 * whenever the wording changes.
 */
const CONSENT_DISCLOSURE = {
  text:
    'I agree to conduct this transaction electronically and intend the ' +
    'typed name above to be my legally binding signature on this document ' +
    '(ESIGN / UETA).',
  version: 'esign-consent-v1',
} as const;

/** Status pill for a signature request — all copy/color from the view model. */
function SigStatusPill({ status }: { status: SignatureRequest['status'] }) {
  const chip = buildStatusChip(status);
  return (
    <span
      className="glass-pill"
      title={chip.title}
      style={{ color: chip.color, background: chip.background }}
    >
      {chip.label}
    </span>
  );
}

function ESignPanel({ doc, canMutate }: { doc: BusinessDoc; canMutate: boolean }) {
  // Hash of the exact version shown when the panel opened — the version the
  // signature will legally cover.
  const signedVersionHash = useMemo(
    () => snapshotVersion(buildModelForDoc(doc)).hash,
    // Freeze at open: later edits must MISMATCH, not silently re-bind.
    [doc.id], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cert, setCert] = useState<CompletionCertificate | null>(null);
  // Real SHA-256 over the canonical doc bytes (crm/docHash) — the hash the
  // signature request is issued over and verified against.
  const [docSha, setDocSha] = useState<string | null>(null);
  // The persisted signature request (signatureRequests collection).
  const [sigReq, setSigReq] = useState<SignatureRequest | null>(null);
  // The tokenized public signing link (capability URL) for the request.
  const [signLink, setSignLink] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    computeDocHash(buildModelForDoc(doc))
      .then((h) => {
        if (!cancelled) setDocSha(h);
      })
      .catch(() => undefined);
    // Surface the most recent request already issued over this doc (if any)
    // so its state chip reflects reality across reloads.
    listSignatureRequestsByDoc(doc.id)
      .then((reqs) => {
        if (!cancelled && reqs.length > 0) setSigReq(reqs[0]);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [doc.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const pad = setTypedValue(createSignaturePad('typed'), name);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const ready = isPadComplete(pad) && emailOk && consent && !busy;

  // Certificate persists across the post-sign status flip.
  if (cert) {
    return (
      <GlassPanel style={{ padding: 16, display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ShieldCheck size={15} color={C.green} />
          <span className="t-section-title">Certificate of completion</span>
          <span
            className="glass-pill"
            style={{
              color: cert.chainOk ? C.green : C.orange,
              fontVariantNumeric: 'tabular-nums',
            }}
            title="Deterministic hash over the full certificate body"
          >
            {cert.chainOk ? 'chain verified' : 'chain BROKEN'} · {cert.certificateHash}
          </span>
          {sigReq && <SigStatusPill status={sigReq.status} />}
          {sigReq && (
            <span
              className="glass-pill"
              title="SHA-256 fingerprint of the exact document the signature covers"
              style={{ color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}
            >
              {buildHashFingerprintLabel(sigReq.docHash)}
            </span>
          )}
        </div>
        <p className="t-meta" style={{ margin: 0 }}>
          {cert.title} — signed document version{' '}
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {cert.docVersionHash}
          </span>
        </p>
        {cert.signers.map((s, i) => (
          <p key={i} className="t-meta" style={{ margin: 0 }}>
            {s.name} &lt;{s.email}&gt; · {s.method} ·{' '}
            {new Date(s.signedAt).toLocaleString()} · {s.ip}
          </p>
        ))}
      </GlassPanel>
    );
  }

  if (!SIGNABLE_STATUSES.includes(doc.status)) return null;

  const sign = async () => {
    setError(null);
    setBusy(true);
    try {
      // Re-hash the doc NOW — signing fails if content changed mid-session.
      const currentHash = snapshotVersion(buildModelForDoc(doc)).hash;
      const signer: Signer = {
        id: `${doc.id}-signer-1`,
        name: padValue(pad),
        email: email.trim(),
        role: 'buyer',
        order: 1,
      };
      const session = initSigning({
        docVersionHash: signedVersionHash,
        signers: [signer],
        order: 'sequential',
      });
      assertDocUnchangedSince(session, currentHash);
      const { state: signedState, record } = captureSignature(session, {
        signerId: signer.id,
        method: 'typed',
        value: padValue(pad),
        consent,
        timestamp: Date.now(),
        ip: 'unavailable (client-side capture)',
        userAgent: navigator.userAgent,
      });
      const verdict = verifySignatureChain(signedState);
      if (!verdict.ok) {
        throw new Error(`Signature chain failed verification at ${verdict.brokenAt}.`);
      }
      const certificate = buildCertificate(signedState, {
        title: doc.title,
        docVersionHash: signedVersionHash,
      });
      // Persist the signature request through its strict state machine
      // (signatureRequests collection): issue over the REAL SHA-256 doc
      // hash, then view → sign with the recorded consent disclosure. Any
      // guard failure (hash drift, missing consent) aborts the whole sign.
      const sha = docSha ?? (await computeDocHash(buildModelForDoc(doc)));
      const issued = await createSignatureRequest({
        docId: doc.id,
        docHash: sha,
        signerName: signer.name,
        signerEmail: signer.email,
        consentDisclosure: CONSENT_DISCLOSURE,
      });
      const viewedStep = applyTransition(issued, {
        type: 'view',
        userAgent: navigator.userAgent,
      });
      const signStep = applyTransition(
        viewedStep.ok && viewedStep.next ? viewedStep.next : issued,
        {
          type: 'sign',
          consent,
          signatureData: { kind: 'typed', value: padValue(pad) },
          docHash: sha,
          userAgent: navigator.userAgent,
        },
      );
      if (!signStep.ok || !signStep.next) {
        throw new Error(signStep.error ?? 'Signature request rejected.');
      }
      const persisted = await updateSignatureRequest(issued.id, signStep.next);
      setSigReq(persisted);
      // Seal the signer event into the tamper-evident Decision Ledger, then
      // run the REAL governed status transition (onboarding spin-up etc.).
      await logActivity({
        event: 'executed',
        source: 'system',
        kind: 'esign',
        subjectId: doc.id,
        summary: `E-signed "${doc.title}" by ${signer.name} <${signer.email}> — doc ${record.docVersionHash}, entry ${record.entryHash}, certificate ${certificate.certificateHash}`,
      });
      await setDocumentStatus(
        doc.id,
        doc.kind === 'proposal' ? 'accepted' : 'signed',
      );
      setCert(certificate);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Issue (or reuse) the persisted signature request and mint its signer
   * link — a capability URL whose token derives from the request id + salt
   * (crm/signerToken.ts), so the link is unguessable from the id alone.
   * The public SignView verifies the token before rendering anything.
   */
  const createLink = async () => {
    setError(null);
    setLinkBusy(true);
    try {
      if (!isPadComplete(pad) || !emailOk) {
        throw new Error(
          "Enter the signer's legal name and email first — the request records who this link is for.",
        );
      }
      const sha = docSha ?? (await computeDocHash(buildModelForDoc(doc)));
      const open =
        sigReq && (sigReq.status === 'sent' || sigReq.status === 'viewed')
          ? sigReq
          : await createSignatureRequest({
              docId: doc.id,
              docHash: sha,
              signerName: padValue(pad),
              signerEmail: email.trim(),
              consentDisclosure: CONSENT_DISCLOSURE,
            });
      setSigReq(open);
      const link = buildSignLink(
        window.location.origin + window.location.pathname,
        open.id,
        issueSignerToken(open.id, DEFAULT_SIGNER_LINK_SALT),
      );
      setSignLink(link);
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        // Clipboard denied — the link is still displayed for manual copy.
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <PenLine size={15} color={C.magentaSoft} />
        <span className="t-section-title">Sign this {doc.kind}</span>
        <span
          className="glass-pill"
          title="snapshotVersion hash of the exact content being signed"
          style={{ color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}
        >
          version {signedVersionHash}
        </span>
        {docSha && (
          <span
            className="glass-pill"
            title="SHA-256 over the canonical document bytes — what you see is what you sign"
            style={{ color: C.textMuted, fontVariantNumeric: 'tabular-nums' }}
          >
            {buildHashFingerprintLabel(docSha)}
          </span>
        )}
        {sigReq && <SigStatusPill status={sigReq.status} />}
      </div>
      {!canMutate ? (
        <p className="t-meta" style={{ margin: 0 }}>
          Viewer role — signing is disabled.
        </p>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            <input
              className="glass-input"
              placeholder="Type your full legal name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              aria-label="Signer legal name"
            />
            <input
              className="glass-input"
              placeholder="Your email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              aria-label="Signer email"
            />
          </div>
          {isPadComplete(pad) && (
            <div
              style={{
                fontFamily: "'Snell Roundhand', 'Segoe Script', cursive",
                fontSize: 24,
                color: C.text,
                borderBottom: '1px solid rgba(255,255,255,0.25)',
                paddingBottom: 4,
              }}
              title="Signature preview — the normalized typed value that will be sealed"
            >
              {padValue(pad)}
            </div>
          )}
          <label
            className="t-meta"
            style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              disabled={busy}
              style={{ marginTop: 2 }}
            />
            <span>
              {/* Rendered from CONSENT_DISCLOSURE so the text the signer
                  sees is byte-identical to the text recorded as evidence. */}
              {CONSENT_DISCLOSURE.text}
            </span>
          </label>
          {error && (
            <p className="t-meta" style={{ margin: 0, color: C.orange }} role="alert">
              {error}
            </p>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              className="glass-btn glass-btn--primary"
              onClick={sign}
              disabled={!ready}
              title={
                ready
                  ? 'Seal a hash-chained signature bound to this exact document version'
                  : 'Enter your legal name and email, and check the consent box'
              }
            >
              <PenLine size={13} /> {busy ? 'Sealing signature…' : 'Sign & execute'}
            </button>
          </div>
          {/* Zero-friction remote path: issue the request now, hand the
              signer a tokenized public link instead of signing inline. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            {signLink && (
              <span
                className="glass-pill"
                title={`${signLink} — copied to clipboard`}
                style={{
                  color: C.textMuted,
                  maxWidth: 380,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {signLink}
              </span>
            )}
            <button
              className="glass-btn"
              onClick={createLink}
              disabled={busy || linkBusy}
              title="Issue the signature request and copy its tokenized public signing link"
            >
              <Send size={13} />{' '}
              {linkBusy
                ? 'Issuing link…'
                : signLink
                  ? 'Copy signing link again'
                  : 'Create signing link'}
            </button>
          </div>
        </>
      )}
    </GlassPanel>
  );
}

function TotalCell({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div className="t-eyebrow">{label}</div>
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: accent ? 800 : 600,
          color: accent ? C.magentaSoft : C.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}
